// src/components/DesignChat.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  MessageSquare,
  Send,
  Loader2,
  Lightbulb,
  RefreshCw,
  Compass,
  User,
  Bot,
  Sparkles,
  HelpCircle,
} from 'lucide-react';

const DesignChat = ({
  chatService,
  presentationAnalysis,
  uploadedFileName,
  knowledgeStats,
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [showTips, setShowTips] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const canChat = useMemo(() => Boolean(chatService), [chatService]);

  useEffect(() => {
    if (chatService) {
      const suggestions = chatService.suggestQuestions(presentationAnalysis);
      setSuggestedQuestions(suggestions);
    } else {
      setSuggestedQuestions([]);
    }
  }, [chatService, presentationAnalysis]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (preFilled = null) => {
    if (!canChat || isLoading) {
      return;
    }

    const messageText = preFilled ?? inputMessage.trim();
    if (!messageText) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };

    const assistantId = userMessage.id + 1;
    const assistantMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      streaming: true,
    };

    const historyForApi = [...messages, userMessage];

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputMessage('');
    setIsLoading(true);
    setShowTips(false);

    try {
      const result = await chatService.sendMessage(
        messageText,
        presentationAnalysis,
        historyForApi,
        (delta, fullContent) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, content: fullContent, streaming: true } : msg
            )
          );
        }
      );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? {
                ...msg,
                streaming: false,
                content: result.content,
                timestamp: result.timestamp,
              }
            : msg
        )
      );
    } catch (error) {
      const fallback = {
        id: assistantId,
        role: 'assistant',
        content: `I encountered an error: ${error.message}. Please try again in a moment.`,
        timestamp: new Date().toISOString(),
        isError: true,
        streaming: false,
      };

      setMessages((prev) =>
        prev.map((msg) => (msg.id === assistantId ? fallback : msg))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setShowTips(true);
    if (chatService) {
      setSuggestedQuestions(chatService.suggestQuestions(presentationAnalysis));
    }
  };

  const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const isError = message.isError;

    return (
      <div className={`flex gap-3 mb-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isUser ? 'bg-blue-500' : isError ? 'bg-red-500' : 'bg-gray-600'
          }`}
        >
          {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
        </div>

        <div className={`max-w-[70%] ${isUser ? 'text-right' : 'text-left'}`}>
          <div
            className={`inline-flex items-start gap-2 px-4 py-2 rounded-lg ${
              isUser
                ? 'bg-blue-500 text-white'
                : isError
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            <div className="whitespace-pre-wrap flex-1">{message.content}</div>
            {message.streaming && !isUser && (
              <Loader2 className="mt-1 h-3.5 w-3.5 animate-spin text-gray-400" />
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {new Date(message.timestamp).toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  };

  const suggested = suggestedQuestions.slice(0, 3);

  return (
    <div className="flex flex-col h-full max-h-[600px]">
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Design Assistant</h3>
          {knowledgeStats?.hasFramework && (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
              Framework Active
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => chatService && setSuggestedQuestions(chatService.suggestQuestions(presentationAnalysis))}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            title="Refresh suggestions"
            disabled={!chatService}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={clearChat}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            title="Clear chat"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {!canChat ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-700">
            Connect to the backend to enable the assistant.
          </div>
        ) : messages.length === 0 ? (
          <div>
            <WelcomeMessage uploadedFileName={uploadedFileName} />
            {showTips && (
              <div>
                <SuggestedQuestions questions={suggested} onSelect={handleSendMessage} />
                <QuickTips presentationAnalysis={presentationAnalysis} />
              </div>
            )}
          </div>
        ) : (
          <div>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(event) => setInputMessage(event.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask about colors, fonts, layouts, or any design decision..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={1}
              style={{ minHeight: '40px', maxHeight: '100px' }}
              disabled={!canChat || isLoading}
            />
          </div>
          <button
            onClick={() => handleSendMessage()}
            disabled={!canChat || !inputMessage.trim() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>

        {messages.length === 0 && suggested.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggested.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSendMessage(question)}
                className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
              >
                {question.length > 40 ? `${question.substring(0, 37)}...` : question}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WelcomeMessage = ({ uploadedFileName }) => (
  <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4">
    <div className="flex items-start gap-3">
      <Compass className="h-5 w-5 text-blue-600" />
      <div>
        <h4 className="font-semibold text-blue-800">Welcome to the Design Compass assistant</h4>
        <p className="mt-1 text-sm text-blue-700">
          Ask anything about <strong>{uploadedFileName || 'your presentation'}</strong> - structure, visuals, storytelling, or how to prioritise fixes.
        </p>
      </div>
    </div>
  </div>
);

const SuggestedQuestions = ({ questions, onSelect }) => {
  if (!questions || questions.length === 0) return null;

  return (
    <div className="mb-6">
      <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Sparkles className="h-4 w-4 text-purple-500" />
        Suggested questions
      </h4>
      <div className="flex flex-wrap gap-2">
        {questions.map((question, index) => (
          <button
            key={index}
            onClick={() => onSelect(question)}
            className="rounded-full border border-purple-200 px-3 py-1 text-xs text-purple-700 hover:border-purple-300 hover:bg-purple-50 transition-colors"
          >
            {question.length > 50 ? `${question.substring(0, 47)}...` : question}
          </button>
        ))}
      </div>
    </div>
  );
};

const QuickTips = ({ presentationAnalysis }) => {
  const tips = useMemo(() => {
    const type = presentationAnalysis?.presentationType?.primary;
    switch (type) {
      case 'Executive & Strategic':
        return [
          'Keep layouts clean, purposeful, and data-led.',
          'Lead with the insight and clarify the "so what" early.',
          'Use conservative color accents (navy, charcoal, muted tints).',
        ];
      case 'Sales & Influence':
        return [
          'Contrast matters: hero your key message on each slide.',
          'Illustrate the value proposition visually (before vs after).',
          'End each section with a micro-call-to-action.',
        ];
      case 'Engagement & Immersion':
        return [
          'Use full-bleed visuals and minimal copy to keep energy high.',
          'Build in narrative beats (setup, tension, resolution).',
          'Aim for emotional descriptors rather than corporate language.',
        ];
      case 'Informative & Educational':
        return [
          'Chunk the content: one idea per slide, backed by visuals.',
          'Use diagrams and iconography to anchor complex concepts.',
          'Repeat key terminology to reinforce learning.'
        ];
      default:
        return [
          'Stay consistent with font sizing hierarchy (title, subtitle, body).',
          'Anchor each slide with a single objective.',
          'Support claims with either data, quotes, or visuals.',
        ];
    }
  }, [presentationAnalysis]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Lightbulb className="h-4 w-4 text-amber-500" /> Quick tips
      </div>
      <ul className="space-y-2 text-sm text-gray-600">
        {tips.map((tip, index) => (
          <li key={index} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DesignChat;






